import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AdminUsersPage = () => {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showChangePermissionsModal, setShowChangePermissionsModal] = useState(false);
  const [showChangeCarrierModal, setShowChangeCarrierModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showNewPasswordModal, setShowNewPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [userTypes, setUserTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [selectedUserTypeId, setSelectedUserTypeId] = useState('');
  const [selectedCarrierId, setSelectedCarrierId] = useState('');

  // Direct Permissions state
  const [showDirectPermissionsModal, setShowDirectPermissionsModal] = useState(false);
  const [directPermissions, setDirectPermissions] = useState([]);
  const [showGrantPermissionModal, setShowGrantPermissionModal] = useState(false);
  const [showEditExpirationModal, setShowEditExpirationModal] = useState(false);
  const [selectedDirectPermission, setSelectedDirectPermission] = useState(null);
  const [selectedPermissionIdToGrant, setSelectedPermissionIdToGrant] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [isPermanent, setIsPermanent] = useState(true);
  const [allPermissions, setAllPermissions] = useState([]);
  
  // Filter and Sort state
  const [selectedCarrierIds, setSelectedCarrierIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCarrierFilterModal, setShowCarrierFilterModal] = useState(false);

  // Get API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

 // Memoized function to fetch users
 const fetchUsers = useCallback(async () => {
   try {
     setLoading(true);
     const token = localStorage.getItem('authToken');
     
     // Build query parameters
     const params = new URLSearchParams();
     
     // Add carrier filters
     if (selectedCarrierIds.length > 0) {
       selectedCarrierIds.forEach(id => params.append('carrier_ids', id));
     }
     
     // Add status filter
     if (statusFilter !== 'all') {
       params.append('status', statusFilter);
     }
     
     // Add sorting
     params.append('sort_by', sortBy);
     params.append('sort_order', sortOrder);
     
     const response = await axios.get(`${API_BASE_URL}/admin/users?${params.toString()}`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     setUsers(response.data.users || []);
   } catch (err) {
     setError('Failed to fetch users');
     console.error('Error fetching users:', err);
   } finally {
     setLoading(false);
   }
 }, [API_BASE_URL, selectedCarrierIds, statusFilter, sortBy, sortOrder]);

 // Memoized function to fetch user types
 const fetchUserTypes = useCallback(async () => {
   try {
     const token = localStorage.getItem('authToken');
     const response = await axios.get(`${API_BASE_URL}/admin/user-types`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     setUserTypes(response.data.user_types || []);
   } catch (err) {
     console.error('Error fetching user types:', err);
     setError('Failed to fetch user types');
   }
 }, [API_BASE_URL]); // Dependencies: API_BASE_URL, setUserTypes

 // Memoized function to fetch carriers
 const fetchCarriers = useCallback(async () => {
   try {
     const token = localStorage.getItem('authToken');
     const response = await axios.get(`${API_BASE_URL}/admin/carriers`, {
       headers: {
         'Authorization': `Bearer ${token}`
       }
     });
     setCarriers(response.data.carriers || []);
   } catch (err) {
     console.error('Error fetching carriers:', err);
     setError('Failed to fetch carriers');
   }
 }, [API_BASE_URL]); // Dependencies: API_BASE_URL, setCarriers

 // Effect to fetch users on component mount
 useEffect(() => {
   if (hasPermission('system.admin')) {
     fetchUsers();
   }
 }, [hasPermission, fetchUsers]);

  const handleActionSelect = (action, user) => {
    setSelectedUser(user);
    if (action === 'deactivate') {
      setPendingAction({
        type: 'deactivate'
      });
      setShowConfirmDialog(true);
    } else if (action === 'reactivate') {
      setPendingAction({
        type: 'reactivate'
      });
      setShowConfirmDialog(true);
    } else if (action === 'remove') {
      setPendingAction({
        type: 'remove'
      });
      setShowConfirmDialog(true);
    } else if (action === 'change_permissions') {
      fetchUserTypes();
      setShowChangePermissionsModal(true);
    } else if (action === 'change_carrier') {
      fetchCarriers();
      setSelectedCarrierId(user.carrier_id || '');
      setShowChangeCarrierModal(true);
    } else if (action === 'reset_password') {
      setShowResetPasswordModal(true);
    } else if (action === 'manage_direct_permissions') {
      fetchDirectPermissions(user.user_id);
      setShowDirectPermissionsModal(true);
    }
  };

  const fetchDirectPermissions = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}/direct-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDirectPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching direct permissions:', err);
      setError('Failed to fetch direct permissions');
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAllPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching all permissions:', err);
      setError('Failed to fetch permissions');
    }
  };

  const handleGrantDirectPermission = async () => {
    if (!selectedUser || !selectedPermissionIdToGrant) return;

    try {
      const token = localStorage.getItem('authToken');

      // Calculate expires_date timestamp if not permanent
      let expiresTimestamp = null;
      if (!isPermanent && expirationDate) {
        expiresTimestamp = Math.floor(new Date(expirationDate).getTime() / 1000);
      }

      await axios.post(
        `${API_BASE_URL}/admin/users/${selectedUser.user_id}/direct-permissions`,
        {
          permission_id: selectedPermissionIdToGrant,
          expires_date: expiresTimestamp
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Refresh direct permissions list
      await fetchDirectPermissions(selectedUser.user_id);

      // Reset form
      setShowGrantPermissionModal(false);
      setSelectedPermissionIdToGrant('');
      setExpirationDate('');
      setIsPermanent(true);
      setError(null);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to grant direct permission');
      console.error('Error granting direct permission:', err);
    }
  };

  const handleEditExpiration = async () => {
    if (!selectedUser || !selectedDirectPermission) return;

    try {
      const token = localStorage.getItem('authToken');

      // Calculate expires_date timestamp if not permanent
      let expiresTimestamp = null;
      if (!isPermanent && expirationDate) {
        expiresTimestamp = Math.floor(new Date(expirationDate).getTime() / 1000);
      }

      await axios.put(
        `${API_BASE_URL}/admin/users/${selectedUser.user_id}/direct-permissions/${selectedDirectPermission.permission_id}`,
        { expires_date: expiresTimestamp },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Refresh direct permissions list
      await fetchDirectPermissions(selectedUser.user_id);

      // Reset form
      setShowEditExpirationModal(false);
      setSelectedDirectPermission(null);
      setExpirationDate('');
      setIsPermanent(true);
      setError(null);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update expiration');
      console.error('Error updating expiration:', err);
    }
  };

  const handleRevokeDirectPermission = async (permissionId) => {
    if (!selectedUser) return;

    if (!window.confirm('Are you sure you want to revoke this direct permission?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');

      await axios.delete(
        `${API_BASE_URL}/admin/users/${selectedUser.user_id}/direct-permissions/${permissionId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Refresh direct permissions list
      await fetchDirectPermissions(selectedUser.user_id);
      setError(null);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke direct permission');
      console.error('Error revoking direct permission:', err);
    }
  };

  const openGrantPermissionModal = () => {
    fetchAllPermissions();
    setShowGrantPermissionModal(true);
  };

  const openEditExpirationModal = (permission) => {
    setSelectedDirectPermission(permission);

    // Set initial values
    if (permission.expires_date) {
      setIsPermanent(false);
      const date = new Date(permission.expires_date * 1000);
      setExpirationDate(date.toISOString().slice(0, 16));
    } else {
      setIsPermanent(true);
      setExpirationDate('');
    }

    setShowEditExpirationModal(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(
        `${API_BASE_URL}/admin/users/${selectedUser.user_id}/reset-password`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setNewPassword(response.data.new_password);
        setShowResetPasswordModal(false);
        setShowNewPasswordModal(true);
      }
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
      console.error('Error resetting password:', err);
    }
  };

  const cancelResetPassword = () => {
    setShowResetPasswordModal(false);
    setSelectedUser(null);
  };

  const closeNewPasswordModal = () => {
    setShowNewPasswordModal(false);
    setNewPassword('');
    setSelectedUser(null);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
  };

  const executeAction = async () => {
    if (!selectedUser || !pendingAction) return;

    try {
      const token = localStorage.getItem('authToken');
      
      if (pendingAction.type === 'deactivate') {
        await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
          is_active: false
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else if (pendingAction.type === 'reactivate') {
        await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
          is_active: true
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else if (pendingAction.type === 'remove') {
        await axios.delete(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      // Refresh the users list
      await fetchUsers();
      
      // Reset state
      setShowConfirmDialog(false);
      setSelectedUser(null);
      setPendingAction(null);
    } catch (err) {
      setError('Failed to update user');
      console.error('Error updating user:', err);
    }
  };

  const cancelAction = () => {
    setShowConfirmDialog(false);
    setSelectedUser(null);
    setPendingAction(null);
  };

  const handleChangePermissions = async () => {
    if (!selectedUser || !selectedUserTypeId) return;

    try {
      const token = localStorage.getItem('authToken');
      
      await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
        user_type_id: selectedUserTypeId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Refresh the users list
      await fetchUsers();
      
      // Reset state
      setShowChangePermissionsModal(false);
      setSelectedUser(null);
      setSelectedUserTypeId('');
      setError(null);
      
    } catch (err) {
      setError('Failed to update user permissions');
      console.error('Error updating user permissions:', err);
    }
  };

  const cancelChangePermissions = () => {
    setShowChangePermissionsModal(false);
    setSelectedUser(null);
    setSelectedUserTypeId('');
  };

  const handleChangeCarrier = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('authToken');
      
      await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
        carrier_id: selectedCarrierId || null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Refresh the users list
      await fetchUsers();
      
      // Reset state
      setShowChangeCarrierModal(false);
      setSelectedUser(null);
      setSelectedCarrierId('');
      setError(null);
      
    } catch (err) {
      setError('Failed to update user carrier');
      console.error('Error updating user carrier:', err);
    }
  };

  const cancelChangeCarrier = () => {
    setShowChangeCarrierModal(false);
    setSelectedUser(null);
    setSelectedCarrierId('');
  };

  // Check permissions after hooks
  if (!hasPermission('system.admin')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-red-600">Error</h2>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchUsers}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleCarrierFilterApply = (carrierIds) => {
    setSelectedCarrierIds(carrierIds);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <span className="ml-1 text-gray-400">⇅</span>;
    }
    return sortOrder === 'asc' ? 
      <span className="ml-1 text-blue-600">↑</span> : 
      <span className="ml-1 text-blue-600">↓</span>;
  };

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="w-full mx-auto">
        <h1 className="text-2xl font-bold mb-6">User Management</h1>
        
        {/* Filter and Sort Controls */}
        <div className="mb-4 bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Carrier Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Carrier:</label>
              <button
                onClick={() => {
                  fetchCarriers();
                  setShowCarrierFilterModal(true);
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {selectedCarrierIds.length > 0 ? `${selectedCarrierIds.length} selected` : 'All Carriers'}
              </button>
              {selectedCarrierIds.length > 0 && (
                <button
                  onClick={() => setSelectedCarrierIds([])}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Results count */}
            <div className="ml-auto text-sm text-gray-600">
              {users.length} user{users.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Login Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('carrier_name')}
                >
                  <div className="flex items-center">
                    Carrier
                    <SortIcon column="carrier_name" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('is_active')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon column="is_active" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_date')}
                >
                  <div className="flex items-center">
                    Created Date
                    <SortIcon column="created_date" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('last_login')}
                >
                  <div className="flex items-center">
                    Last Login
                    <SortIcon column="last_login" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {user.user_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {user.display_name || `${user.first_name} ${user.last_name}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {user.permission}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {user['Login Method']}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {user['Login Method'] === 'local' ? user.username || 'N/A' : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {user.carrier_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {new Date(user.created_date * 1000).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {user.last_login ? new Date(user.last_login * 1000).toLocaleString() : 'N/A'} {/* Changed toLocaleDateString to toLocaleString */}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    <select
                      className="border border-gray-300 rounded px-3 py-1 text-sm"
                      onChange={(e) => {
                        if (e.target.value) {
                          handleActionSelect(e.target.value, user);
                          e.target.value = '';
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="">Select Action</option>
                      <option value="change_permissions">Change Permissions</option>
                      <option value="manage_direct_permissions">Manage Direct Permissions</option>
                      <option value="change_carrier">Change Carrier</option>
                      {user['Login Method'] === 'local' && (
                        <option value="reset_password">Reset Password</option>
                      )}
                      {user.is_active ? (
                        <>
                          <option value="deactivate">Deactivate</option>
                          <option value="remove">Remove</option>
                        </>
                      ) : (
                        <>
                          <option value="reactivate">Reactivate</option>
                          <option value="remove">Remove</option>
                        </>
                      )}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {users.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No users found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
            
            {pendingAction?.type === 'deactivate' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to deactivate the user "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
                <br /><br />
                This will prevent them from logging in but will preserve their account data.
              </p>
            )}
            
            {pendingAction?.type === 'reactivate' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to reactivate the user "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
                <br /><br />
                This will allow them to log in again and access the system.
              </p>
            )}
            
            {pendingAction?.type === 'remove' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove the user "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
                <br /><br />
                <span className="text-red-600 font-semibold">This action will deactivate the user account and cannot be easily undone.</span>
              </p>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelAction}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                className={`px-4 py-2 text-white rounded hover:opacity-90 ${
                  pendingAction?.type === 'remove' 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {pendingAction?.type === 'remove' 
                  ? 'Remove User' 
                  : pendingAction?.type === 'reactivate' 
                    ? 'Reactivate User' 
                    : 'Deactivate User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Carrier Modal */}
      {showChangeCarrierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Change User Carrier</h3>
            
            <p className="text-gray-600 mb-4">
              Change carrier for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}":
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Carrier: <span className="font-semibold">{selectedUser?.carrier_name || 'None'}</span>
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Carrier
              </label>
              <select
                value={selectedCarrierId}
                onChange={(e) => setSelectedCarrierId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">No Carrier (System User)</option>
                {carriers.map((carrier) => (
                  <option key={carrier.carrier_id} value={carrier.carrier_id}>
                    {carrier.carrier_name}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-2">
                Select "No Carrier" to make this a system-wide user not associated with any carrier.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelChangeCarrier}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeCarrier}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Update Carrier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reset Password</h3>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to reset the password for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
              <br /><br />
              A new secure 8-character password will be generated and shown to you. You will need to share this password with the user.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelResetPassword}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Password Display Modal */}
      {showNewPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Password Reset Successful</h3>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                The password for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}" has been reset.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-400 rounded p-4 mb-4">
                <p className="text-yellow-800 font-semibold mb-2">⚠️ Important Warning</p>
                <p className="text-yellow-700 text-sm">
                  This is the only time you will see this password. Make sure to copy it now and share it with the user.
                </p>
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newPassword}
                  readOnly
                  className="flex-1 border border-gray-300 rounded px-3 py-2 bg-gray-50 font-mono text-lg"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={closeNewPasswordModal}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Permissions Modal */}
      {showChangePermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Change User Permissions</h3>
            
            <p className="text-gray-600 mb-4">
              Change permissions for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}":
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Permission: <span className="font-semibold">{selectedUser?.permission}</span>
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Permission *
              </label>
              <select
                value={selectedUserTypeId}
                onChange={(e) => setSelectedUserTypeId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              >
                <option value="">Select New Permission</option>
                {userTypes.map((type) => (
                  <option key={type.user_type_id} value={type.user_type_id}>
                    {type.type_name} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelChangePermissions}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePermissions}
                disabled={!selectedUserTypeId}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Permissions Management Modal */}
      {showDirectPermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <h3 className="text-lg font-semibold mb-4">
              Direct Permissions for {selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              Direct permissions are assigned specifically to this user and override or supplement their user type permissions.
              These can be permanent or set to expire after a specific date.
            </p>

            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Current Direct Permissions</h4>
              <button
                onClick={openGrantPermissionModal}
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
              >
                + Grant Permission
              </button>
            </div>

            <div className="flex-1 overflow-y-auto mb-4">
              {directPermissions.length > 0 ? (
                <div className="space-y-2">
                  {directPermissions.map((permission) => (
                    <div key={permission.permission_id} className="p-3 border rounded bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold">{permission.permission_name}</div>
                          <div className="text-sm text-gray-600">{permission.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {permission.resource_type} / {permission.action_type}
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            Granted: {new Date(permission.granted_date * 1000).toLocaleString()}
                            {permission.granted_by_name && ` by ${permission.granted_by_name}`}
                          </div>
                          {permission.expires_date ? (
                            <div className="text-xs mt-1">
                              <span className={`font-semibold ${
                                permission.expires_date < Math.floor(Date.now() / 1000)
                                  ? 'text-red-600'
                                  : 'text-orange-600'
                              }`}>
                                Expires: {new Date(permission.expires_date * 1000).toLocaleString()}
                              </span>
                            </div>
                          ) : (
                            <div className="text-xs text-green-600 font-semibold mt-1">
                              Permanent
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-1 ml-2">
                          <button
                            onClick={() => openEditExpirationModal(permission)}
                            className="px-2 py-1 text-xs bg-blue-200 rounded hover:bg-blue-300"
                          >
                            Edit Expiration
                          </button>
                          <button
                            onClick={() => handleRevokeDirectPermission(permission.permission_id)}
                            className="px-2 py-1 text-xs bg-red-200 text-red-700 rounded hover:bg-red-300"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No direct permissions assigned to this user
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowDirectPermissionsModal(false);
                  setSelectedUser(null);
                  setDirectPermissions([]);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Direct Permission Modal */}
      {showGrantPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Grant Direct Permission</h3>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Permission *
              </label>
              <select
                value={selectedPermissionIdToGrant}
                onChange={(e) => setSelectedPermissionIdToGrant(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              >
                <option value="">Select a permission</option>
                {allPermissions.map((permission) => (
                  <option key={permission.permission_id} value={permission.permission_id}>
                    {permission.permission_name} - {permission.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={isPermanent}
                  onChange={(e) => {
                    setIsPermanent(e.target.checked);
                    if (e.target.checked) {
                      setExpirationDate('');
                    }
                  }}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Permanent Permission</span>
              </label>

              {!isPermanent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required={!isPermanent}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The permission will automatically expire at this date and time
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGrantPermissionModal(false);
                  setSelectedPermissionIdToGrant('');
                  setExpirationDate('');
                  setIsPermanent(true);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGrantDirectPermission}
                disabled={!selectedPermissionIdToGrant || (!isPermanent && !expirationDate)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Grant Permission
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Expiration Modal */}
      {showEditExpirationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Permission Expiration</h3>

            <p className="text-sm text-gray-600 mb-4">
              Editing expiration for: <span className="font-semibold">{selectedDirectPermission?.permission_name}</span>
            </p>

            <div className="mb-6">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={isPermanent}
                  onChange={(e) => {
                    setIsPermanent(e.target.checked);
                    if (e.target.checked) {
                      setExpirationDate('');
                    }
                  }}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Make Permanent</span>
              </label>

              {!isPermanent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required={!isPermanent}
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditExpirationModal(false);
                  setSelectedDirectPermission(null);
                  setExpirationDate('');
                  setIsPermanent(true);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditExpiration}
                disabled={!isPermanent && !expirationDate}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carrier Filter Modal */}
      {showCarrierFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Filter by Carrier</h2>
            </div>

            {selectedCarrierIds.length > 0 && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <span className="text-sm font-medium text-gray-600 mr-2">Selected:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {carriers
                    .filter(c => selectedCarrierIds.includes(String(c.carrier_id)))
                    .map(carrier => (
                      <span key={carrier.carrier_id} className="flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                        {carrier.carrier_name}
                        <button
                          onClick={() => setSelectedCarrierIds(prev => prev.filter(id => id !== String(carrier.carrier_id)))}
                          className="ml-1.5 text-blue-600 hover:text-blue-800 focus:outline-none"
                          aria-label={`Remove ${carrier.carrier_name}`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                </div>
              </div>
            )}

            <div className="p-4 overflow-y-auto flex-grow">
              <ul className="space-y-2">
                {carriers.map(carrier => (
                  <li key={carrier.carrier_id} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      id={`carrier-${carrier.carrier_id}`}
                      checked={selectedCarrierIds.includes(String(carrier.carrier_id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCarrierIds(prev => [...prev, String(carrier.carrier_id)]);
                        } else {
                          setSelectedCarrierIds(prev => prev.filter(id => id !== String(carrier.carrier_id)));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                    />
                    <label htmlFor={`carrier-${carrier.carrier_id}`} className="text-sm text-gray-700 cursor-pointer">
                      {carrier.carrier_name}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50">
              <button
                onClick={() => setShowCarrierFilterModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleCarrierFilterApply(selectedCarrierIds);
                  setShowCarrierFilterModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
